/**
 * AudioDirector — WebAudio synthesis, no binary assets (spec §15.1).
 * The iOS app mirrors these voicings with AVAudioEngine source nodes.
 *
 * Pickup notes ride a major-pentatonic ladder so any combo rhythm the player
 * produces is musical; each echo owns an identity tone reused by its warning,
 * unlock, and the clear chord (spec §15.2's "temporal audio" idea).
 */

export type InstrumentId = "puresine" | "softpluck" | "octave" | "glassbells" | "deeppad";

const PENTATONIC = [392.0, 440.0, 493.88, 587.33, 659.25, 783.99, 880.0, 987.77]; // G4 A4 B4 D5 E5 G5 A5 B5
const ECHO_TONES: Record<string, number> = { A: 523.25, B: 392.0, C: 659.25, D: 329.63 };

export class AudioDirector {
  private ctx: AudioContext | null = null;
  private fxGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private musicNodes: { osc: OscillatorNode; gain: GainNode }[] = [];
  private stems = new Map<string, { osc: OscillatorNode; lfo: OscillatorNode; gain: GainNode }>();

  fxEnabled = true;
  musicEnabled = true;
  instrument: InstrumentId = "puresine";

  ensure(): AudioContext | null {
    if (!this.ctx) {
      try {
        this.ctx = new AudioContext();
        this.fxGain = this.ctx.createGain();
        this.fxGain.gain.value = 0.9;
        this.fxGain.connect(this.ctx.destination);
        this.musicGain = this.ctx.createGain();
        this.musicGain.gain.value = 0.0;
        this.musicGain.connect(this.ctx.destination);
      } catch {
        return null;
      }
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
    return this.ctx;
  }

  private note(
    freq: number,
    dur: number,
    type: OscillatorType,
    gain: number,
    when = 0,
    dest?: AudioNode,
  ): void {
    const c = this.ensure();
    if (!c || !this.fxGain) return;
    const t = c.currentTime + when;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = type;
    o.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(gain, 0.0002), t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g);
    g.connect(dest ?? this.fxGain);
    o.start(t);
    o.stop(t + dur + 0.05);
  }

  private fx(fn: () => void): void {
    if (this.fxEnabled) fn();
  }

  touchDown(): void {
    this.fx(() => this.note(1245, 0.03, "sine", 0.012));
  }

  /** Run-start identity: a rising two-note gesture that resolves into the drone. */
  runStart(): void {
    this.fx(() => {
      this.note(392.0, 0.18, "sine", 0.05);
      this.note(587.33, 0.34, "sine", 0.06, 0.12);
    });
  }

  pickup(combo: number): void {
    this.fx(() => {
      const f = PENTATONIC[Math.min(combo, 8) - 1];
      switch (this.instrument) {
        case "softpluck":
          this.note(f, 0.16, "triangle", 0.11);
          this.note(f * 2, 0.1, "triangle", 0.03);
          break;
        case "octave":
          this.note(f, 0.2, "sine", 0.09);
          this.note(f * 2, 0.22, "sine", 0.055, 0.02);
          break;
        case "glassbells":
          this.note(f, 0.5, "sine", 0.07);
          this.note(f * 2.756, 0.34, "sine", 0.022, 0.005); // inharmonic bell partial
          this.note(f * 5.404, 0.2, "sine", 0.01, 0.005);
          break;
        case "deeppad":
          this.note(f / 2, 0.4, "sawtooth", 0.035);
          this.note(f, 0.3, "sine", 0.06, 0.01);
          break;
        default:
          this.note(f, 0.22, "sine", 0.09);
          this.note(f * 2, 0.14, "sine", 0.03);
      }
    });
  }

  comboTimeout(): void {
    this.fx(() => {
      this.note(493.88, 0.12, "sine", 0.028);
      this.note(392.0, 0.14, "sine", 0.028, 0.1);
    });
  }

  warnTick(): void {
    this.fx(() => this.note(1160, 0.05, "square", 0.016));
  }

  unlock(echoId: string): void {
    this.fx(() => {
      const tone = ECHO_TONES[echoId] ?? 440;
      // reversed-breath: rising noise-ish sweep approximated with a detuned saw fade-in
      this.note(tone / 4, 0.36, "sawtooth", 0.024);
      this.note(tone, 0.3, "sine", 0.05, 0.1);
    });
  }

  hit(): void {
    this.fx(() => {
      this.note(92, 0.26, "square", 0.09);
      this.note(58, 0.3, "sine", 0.09, 0.02);
    });
  }

  secondPulse(): void {
    this.fx(() => this.note(196, 0.07, "sine", 0.045));
  }

  clear(activeEchoIds: string[]): void {
    this.fx(() => {
      const base = [523.25, 659.25, 783.99, 1046.5];
      const tones = activeEchoIds.length >= 4 ? activeEchoIds.map((id) => ECHO_TONES[id] * 2) : base;
      tones.slice(0, 4).forEach((f, i) => this.note(f, 0.62, "sine", 0.05, i * 0.09));
    });
  }

  gameOver(): void {
    this.fx(() => {
      this.note(311.13, 0.7, "sine", 0.045);
      this.note(369.99, 0.7, "sine", 0.035, 0.05);
    });
  }

  // ---- adaptive ambient music (spec §15.2): base drone + one stem per echo ----

  startMusic(): void {
    const c = this.ensure();
    if (!c || !this.musicGain || !this.musicEnabled) return;
    this.stopMusic();
    this.musicGain.gain.cancelScheduledValues(c.currentTime);
    this.musicGain.gain.setValueAtTime(0.0001, c.currentTime);
    this.musicGain.gain.exponentialRampToValueAtTime(0.16, c.currentTime + 1.2);
    for (const [freq, detune, level] of [
      [49.0, 0, 0.5],
      [98.0, 4, 0.3],
      [146.83, -3, 0.16],
    ] as const) {
      const osc = c.createOscillator();
      osc.type = "sine";
      osc.frequency.value = freq;
      osc.detune.value = detune;
      const gain = c.createGain();
      gain.gain.value = level;
      osc.connect(gain);
      gain.connect(this.musicGain);
      osc.start();
      this.musicNodes.push({ osc, gain });
    }
  }

  addStem(echoId: string): void {
    const c = this.ensure();
    if (!c || !this.musicGain || !this.musicEnabled || this.stems.has(echoId)) return;
    const tone = (ECHO_TONES[echoId] ?? 440) / 2;
    const osc = c.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = tone;
    const gain = c.createGain();
    gain.gain.value = 0.0;
    const lfo = c.createOscillator();
    lfo.frequency.value = { A: 0.42, B: 0.22, C: 0.74, D: 0.16 }[echoId] ?? 0.3; // delay-linked breathing
    const lfoGain = c.createGain();
    lfoGain.gain.value = 0.05;
    lfo.connect(lfoGain);
    lfoGain.connect(gain.gain);
    gain.gain.setValueAtTime(0.0001, c.currentTime);
    gain.gain.linearRampToValueAtTime(0.055, c.currentTime + 0.8);
    osc.connect(gain);
    gain.connect(this.musicGain);
    osc.start();
    lfo.start();
    this.stems.set(echoId, { osc, lfo, gain });
  }

  stopMusic(fade = 0.4): void {
    const c = this.ctx;
    if (!c || !this.musicGain) return;
    this.musicGain.gain.cancelScheduledValues(c.currentTime);
    this.musicGain.gain.setValueAtTime(Math.max(this.musicGain.gain.value, 0.0001), c.currentTime);
    this.musicGain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + fade);
    const nodes = [...this.musicNodes];
    const stems = [...this.stems.values()];
    this.musicNodes = [];
    this.stems.clear();
    setTimeout(() => {
      nodes.forEach((n) => {
        try {
          n.osc.stop();
        } catch { /* already stopped */ }
      });
      stems.forEach((s) => {
        try {
          s.osc.stop();
          s.lfo.stop();
        } catch { /* already stopped */ }
      });
    }, (fade + 0.1) * 1000);
  }
}

/** Best-effort web haptics (iOS Safari has none; the native app uses CoreHaptics). */
export function vibrate(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* unsupported */
  }
}
